import { createApi } from '@reduxjs/toolkit/dist/query/react';
import type { ReportRealTimeSpec } from './ReportRealTime.model';
import {
  Report,
  ReportDataResponseAPI,
  ReportQuery,
  ReportQueryParams,
  ReportRequest,
  ReportSchedule,
  ReportSumParamsAPI,
  ReportSumResponseAPI,
  ReportType,
} from './Report.model';
import { serializeReportSpec } from './Report.util';
import Environment from '../../shared/constants/Environment';
import axiosBaseQuery from '../../service/shared/axiosBaseQuery';
import { toBase64 } from '../../shared/util/base64';

const ReportBaseURL = Environment.backendAPIReports;
export const ReportAPI = createApi({
  reducerPath: 'report',
  tagTypes: ['report', 'reportSchedule', 'reportType'],
  baseQuery: axiosBaseQuery({ baseUrl: ReportBaseURL }),
  endpoints: (build) => ({
    // Reports
    getReports: build.query<ReportQuery, ReportQueryParams>({
      query: (params) => ({
        url: `/main/workspace/${toBase64(params.workspaceId ?? '')}/reports`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
      // queryFn: (params) => ({ data: reportsMock as Report[] }),
    }),
    getReport: build.query<Report, { reportId: string; workspaceId: string }>({
      query: ({ reportId, workspaceId }) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/reports/${reportId}`,
        method: 'GET',
      }),
      providesTags: ['report'],
    }),
    getReportData: build.query<
      ReportDataResponseAPI[],
      { reportId: string; workspaceId: string }
    >({
      query: ({ reportId, workspaceId }) => ({
        url: `/data/workspace/${toBase64(workspaceId)}/reports/${reportId}`,
        method: 'GET',
      }),
      providesTags: ['report'],
    }),
    getReportDataFromSchedule: build.query<
      ReportDataResponseAPI[],
      {
        scheduleId: string;
        workspaceId: string;
        params?: { from?: number; to?: number };
      }
    >({
      query: ({ scheduleId, workspaceId, params }) => ({
        url: `/data/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleId}`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
    }),
    createReport: build.mutation<
      any,
      { spec: ReportRequest; workspaceId: string | undefined }
    >({
      query: ({ spec, workspaceId }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId || ''
        )}/reports?async=true`,
        method: 'POST',
        data: serializeReportSpec(spec),
      }),
      invalidatesTags: ['report'],
    }),
    deleteReport: build.mutation<
      any,
      { reportId: string; workspaceId: string }
    >({
      query: ({ reportId, workspaceId }) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['report'],
    }),
    // Report Schedules
    getReportSchedules: build.query<
      ReportSchedule[],
      { workspaceId: string; groupId?: string }
    >({
      query: ({ workspaceId, groupId }) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/report-schedules`,
        method: 'GET',
        params: { groupId },
      }),
      providesTags: ['reportSchedule'],
    }),
    getReportSchedule: build.query<
      any,
      { workspaceId: string; scheduleID: string }
    >({
      query: ({ workspaceId, scheduleID }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleID}`,
        method: 'GET',
      }),
      providesTags: ['reportSchedule'],
    }),
    createReportSchedule: build.mutation<
      any,
      { workspaceId: string; schedule: any }
    >({
      query: ({ workspaceId, schedule }) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/report-schedules`,
        method: 'POST',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    updateReportSchedule: build.mutation<
      any,
      { workspaceId: string; scheduleID: string; schedule: any }
    >({
      query: ({ workspaceId, scheduleID, schedule }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleID}`,
        method: 'PUT',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    deleteReportSchedule: build.mutation<
      any,
      { workspaceId: string; scheduleID: string; schedule?: any }
    >({
      query: ({ workspaceId, scheduleID, schedule }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleID}`,
        method: 'DELETE',
        data: schedule,
      }),
      invalidatesTags: ['reportSchedule'],
    }),
    // Report Types
    getReportTypes: build.query<ReportType[], string>({
      query: (workspaceId) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/report-types`,
        method: 'GET',
      }),
      //mocking
      // queryFn: () => ({ data: reportTypeMocks }),
      providesTags: ['reportType'],
    }),
    getReportType: build.query<
      any,
      { workspaceId: string; reportTypeID: string }
    >({
      query: ({ workspaceId, reportTypeID }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-types/${reportTypeID}`,
        method: 'GET',
      }),
      providesTags: ['reportType'],
    }),
    createReportType: build.mutation<
      any,
      { workspaceId: string; schedule: any }
    >({
      query: ({ workspaceId, schedule }) => ({
        url: `/main/workspace/${toBase64(workspaceId)}/report-types`,
        method: 'POST',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    updateReportType: build.mutation<
      any,
      { workspaceId: string; reportTypeID: string; schedule: any }
    >({
      query: ({ workspaceId, reportTypeID, schedule }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-types/${reportTypeID}`,
        method: 'PUT',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    deleteReportType: build.mutation<
      any,
      { workspaceId: string; reportTypeID: string; schedule?: any }
    >({
      query: ({ workspaceId, reportTypeID, schedule }) => ({
        url: `/main/workspace/${toBase64(
          workspaceId
        )}/report-types/${reportTypeID}`,
        method: 'DELETE',
        data: schedule,
      }),
      invalidatesTags: ['reportType'],
    }),
    // Report Schedule Sum
    getReportScheduleSum: build.query<
      ReportSumResponseAPI,
      { workspaceId: string; scheduleId: string; params?: ReportSumParamsAPI }
    >({
      query: ({ workspaceId, scheduleId, params }) => ({
        url: `/data/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleId}/sum`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
    }),
    getReportScheduleSumByUser: build.query<
      ReportSumResponseAPI,
      { workspaceId: string; scheduleId: string; params?: ReportSumParamsAPI }
    >({
      query: ({ workspaceId, scheduleId, params }) => ({
        url: `/data/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleId}/sum/user`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
    }),
    getReportScheduleSumByGroup: build.query<
      ReportSumResponseAPI,
      {
        workspaceId: string;
        scheduleId: string;
        groupId: string;
        params?: ReportSumParamsAPI;
      }
    >({
      query: ({ workspaceId, scheduleId, groupId, params }) => ({
        url: `/data/workspace/${toBase64(
          workspaceId
        )}/report-schedules/${scheduleId}/sum/group/${toBase64(groupId)}`,
        method: 'GET',
        params,
      }),
      providesTags: ['report'],
    }),
    /**
     * Aggregates the schedule sum across multiple workspaces.
     * For each workspaceId:
     *  1. Fetches report types → finds the 'PowerConsumption' type
     *  2. Fetches report schedules → finds the schedule for that type
     *  3. Fetches the schedule sum
     * Then reduces all sums into a single ReportSumResponseAPI.
     */
    getReportManyWorkspacesScheduleSum: build.query<
      ReportSumResponseAPI,
      { workspaceIds: string[]; params?: ReportSumParamsAPI }
    >({
      async queryFn(
        { workspaceIds, params },
        _queryApi,
        _extraOptions
      ): Promise<
        | { data: ReportSumResponseAPI }
        | { error: { status: number; data: string; message: string } }
      > {
        const REPORT_TYPE_CONSUMPTION = 'PowerConsumption';
        try {
          const sumResults: number[] = await Promise.all(
            workspaceIds.map(async (workspaceId): Promise<number> => {
              // 1. Get report types (leverages RTK Query cache)
              const typesResult = await _queryApi.dispatch(
                ReportAPI.endpoints.getReportTypes.initiate(workspaceId)
              );
              if (typesResult.error || !typesResult.data) return 0;
              const consumptionType = typesResult.data.find(
                (rt: ReportType) => rt.name === REPORT_TYPE_CONSUMPTION
              );
              if (!consumptionType) return 0;

              // 2. Get report schedules (leverages RTK Query cache)
              const schedulesResult = await _queryApi.dispatch(
                ReportAPI.endpoints.getReportSchedules.initiate({
                  workspaceId,
                })
              );
              if (schedulesResult.error || !schedulesResult.data) return 0;
              const schedule = schedulesResult.data.find(
                (s: ReportSchedule) =>
                  s.reportSpec.reportType?.id === consumptionType.id
              );
              if (!schedule) return 0;

              // 3. Get the schedule sum (leverages RTK Query cache)
              const sumResult: {
                data?: ReportSumResponseAPI;
                error?: unknown;
              } = await _queryApi.dispatch(
                ReportAPI.endpoints.getReportScheduleSum.initiate({
                  workspaceId,
                  scheduleId: schedule.id,
                  params,
                })
              );
              if (sumResult.error || !sumResult.data) return 0;
              return sumResult.data.powerUsage_kJ ?? 0;
            })
          );

          const totalKJ: number = sumResults.reduce(
            (acc: number, val: number) => acc + val,
            0
          );
          return { data: { powerUsage_kJ: totalKJ } };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: 'Failed to fetch multi-workspace schedule sum',
              message: String(error),
            },
          };
        }
      },
      providesTags: ['report'],
    }),

    // Test endpoints
    createReportWithoutPersisting: build.mutation<any, ReportRealTimeSpec>({
      query: (spec) => ({
        url: `/main/reports/test`,
        method: 'POST',
        data: spec,
      }),
    }),
    createReportWithoutPersistingPDF: build.mutation<Blob, ReportRealTimeSpec>({
      query: (spec) => ({
        url: `/main/reports/test/pdf`,
        method: 'POST',
        data: spec,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      }),
    }),
  }),
});

export default ReportAPI;
